import React, { useState } from 'react';
import { heartOptions, riskFactors, riskLevel, timiCriteria } from '../data';
import { ChoiceField, CheckCards } from './Fields';
import EcgExplorer from './EcgExplorer';
import HistoryQuiz from './HistoryQuiz';
import TroponinMeter from './TroponinMeter';

function Result({ type, score, total }) {
  const level = riskLevel(score, type);
  const percentage = type === 'HEART' && level ? { Baixo: '≈ 1–2,5%', Intermediário: '≈ 12–20%', Alto: '≈ 50–73%' }[level] : null;
  return (
    <div className={`result ${level ? `risk-${level.toLowerCase().replace('á', 'a')}` : ''}`} aria-live="polite">
      <div><span className="eyebrow">RESULTADO {type}</span><div className="score">{score === null ? '—' : score}<span> / {total}</span></div></div>
      <div className="result-detail">
        <div className="traffic" aria-label={level ? `Semáforo: risco ${level.toLowerCase()}` : 'Semáforo: cálculo incompleto'} tabIndex="0">
          {['Baixo', 'Intermediário', 'Alto'].map((item) => <span key={item} className={level === item ? 'active' : ''} title={item} />)}
        </div>
        <strong>{level ? `Risco ${level.toLowerCase()}` : 'Preencha todos os componentes'}</strong>
        {percentage && <p>{percentage} de eventos cardíacos adversos maiores em 6 semanas, em estudos de coorte. Estimativas variam conforme a população.</p>}
        {level && type === 'TIMI' && <p>Categoria de risco TIMI; não equivale ao percentual do HEART.</p>}
      </div>
    </div>
  );
}

export default function Calculator() {
  const [mode, setMode] = useState('HEART');
  const [history, setHistory] = useState(null);
  const [ecg, setEcg] = useState(null);
  const [age, setAge] = useState(null);
  const [factors, setFactors] = useState([]);
  const [prior, setPrior] = useState(false);
  const [measured, setMeasured] = useState('');
  const [limit, setLimit] = useState('');
  const [timi, setTimi] = useState([]);
  const toggle = (list, setList, index) => setList(list.includes(index) ? list.filter((item) => item !== index) : [...list, index]);
  const ratio = measured !== '' && limit !== '' && Number(measured) >= 0 && Number(limit) > 0 ? Number(measured) / Number(limit) : null;
  const troponin = ratio === null ? null : ratio <= 1 ? 0 : ratio <= 3 ? 1 : 2;
  const factorPoints = prior || factors.length >= 3 ? 2 : factors.length >= 1 ? 1 : 0;
  const heartScore = [history, ecg, age, troponin].every((value) => value !== null) ? history + ecg + age + factorPoints + troponin : null;
  const showHeart = mode !== 'TIMI';
  const showTimi = mode !== 'HEART';

  return (
    <section id="calculadora" className="calculator-section" aria-labelledby="calc-title">
      <div className="section-heading"><span className="eyebrow">FERRAMENTA INTERATIVA</span><h2 id="calc-title">Como você quer calcular?</h2><p>Selecione um escore, preencha os critérios e veja a classificação atualizar em tempo real.</p></div>
      <div className="mode-switch" role="group" aria-label="Fonte de cálculo">
        {['HEART', 'TIMI', 'Comparar escores'].map((item) => <button key={item} type="button" className={mode === item ? 'active' : ''} aria-pressed={mode === item} onClick={() => setMode(item)}>{item}</button>)}
      </div>
      <div className="calc-layout">
        <div className="form-stack">
          {showHeart && <div className="panel">
            <div className="panel-top"><span className="badge">01</span><div><h3>Escore HEART</h3><p>Cinco componentes · até 10 pontos</p></div></div>
            <ChoiceField title="História" options={heartOptions.history} value={history} onChange={setHistory} />
            <HistoryQuiz onSuggest={setHistory} />
            <EcgExplorer value={ecg} onChange={setEcg} />
            <ChoiceField title="Idade" options={heartOptions.age} value={age} onChange={setAge} />
            <CheckCards title="Fatores de risco" items={riskFactors} selected={factors} onToggle={(index) => toggle(factors, setFactors, index)} />
            <label className={`check-card prior ${prior ? 'selected' : ''}`}><input type="checkbox" checked={prior} onChange={(event) => setPrior(event.target.checked)} /><span>Doença aterosclerótica prévia: infarto, revascularização, AVC ou AIT, ou doença arterial periférica</span></label>
            <p className="field-note">Selecionados: {factors.length} fator(es){prior ? ' + doença prévia' : ''} · {factorPoints} {factorPoints === 1 ? 'ponto' : 'pontos'}. Nenhum = 0; um ou dois = 1; três ou mais ou doença prévia = 2.</p>
            <TroponinMeter measured={measured} limit={limit} onMeasured={setMeasured} onLimit={setLimit} />
          </div>}
          {showTimi && <div className="panel">
            <div className="panel-top"><span className="badge">02</span><div><h3>Escore TIMI</h3><p>Angina instável e IAMSSST · até 7 pontos</p></div></div>
            <p className="field-note">Marque somente os critérios presentes. Cada item vale 1 ponto. Os campos TIMI são independentes dos campos HEART para permitir comparar informações diferentes.</p>
            <CheckCards title="Critérios TIMI" items={timiCriteria} selected={timi} onToggle={(index) => toggle(timi, setTimi, index)} />
          </div>}
        </div>
        <aside className="results-column" aria-label="Resultados dos escores">
          {showHeart && <Result type="HEART" score={heartScore} total={10} />}
          {showTimi && <Result type="TIMI" score={timi.length} total={7} />}
          <div className="notice"><strong>Importante</strong><p>Os escores estimam risco e não substituem avaliação médica nem o protocolo do serviço. Não use o resultado isoladamente para decidir cuidados.</p></div>
        </aside>
      </div>
    </section>
  );
}
